import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth.jsx';
import { ROUTES } from '../lib/constants';
import Button from '../components/common/Button';
import logoImg from '../assets/logo.png';

export default function Register() {
    const navigate = useNavigate();
    const { signUp } = useAuth();

    const [formData, setFormData] = useState({
        nome_completo: '',
        telefone: '',
        placa_veiculo: '',
        email: '',
        password: '',
        confirmPassword: ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    // Capturar código da URL se existir
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const code = params.get('code');
        if (code) {
            console.log('Código capturado da URL:', code);
            // Podemos salvar no sessionStorage para usar após o login/cadastro
            sessionStorage.setItem('pending_student_code', code);
        }
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (formData.password !== formData.confirmPassword) {
            setError('As senhas não coincidem.');
            return;
        }

        if (formData.password.length < 6) {
            setError('A senha deve ter pelo menos 6 caracteres.');
            return;
        }

        setLoading(true);

        try {
            const data = await signUp(formData.email, formData.password, {
                nome_completo: formData.nome_completo,
                telefone: formData.telefone,
                placa_veiculo: formData.placa_veiculo,
                tipo: 'responsavel',
                perfil: 'pai'
            });

            // Se o Supabase retonou sessão, o usuário já está logado (e-mail conf. desativado)
            if (data?.session) {
                navigate(ROUTES.APP_PARENT);
            } else {
                // Se não retornou sessão, é provável que precise confirmar o e-mail
                setSuccess(true);
            }
        } catch (err) {
            console.error('Erro no cadastro:', err);
            setError(err.message || 'Erro ao criar conta. Tente novamente.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-green-50 to-white flex items-center justify-center p-4 py-12">
            <div className="max-w-md w-full">
                {/* Logo */}
                <Link to={ROUTES.HOME} className="flex flex-col items-center justify-center gap-3 mb-8">
                    <img src={logoImg} alt="Fila0 Logo" className="h-24 w-auto object-contain" />
                </Link>

                {/* Card de Cadastro */}
                <div className="card shadow-2xl rounded-3xl p-8 bg-white border border-gray-100">
                    {success ? (
                        <div className="text-center animate-fade-in">
                            <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-6 text-4xl">
                                📧
                            </div>
                            <h2 className="text-3xl font-black text-gray-900 mb-4 tracking-tighter">
                                Verifique seu e-mail
                            </h2>
                            <p className="text-gray-500 mb-8 font-medium leading-relaxed">
                                Enviamos um link de confirmação para <span className="text-gray-900 font-bold">{formData.email}</span>.<br />
                                Por favor, clique no link para ativar sua conta e começar a usar o Fila0.
                            </p>
                            <Button
                                variant="primary"
                                className="w-full py-4 rounded-2xl shadow-lg"
                                onClick={() => navigate(ROUTES.LOGIN)}
                            >
                                IR PARA O LOGIN
                            </Button>
                        </div>
                    ) : (
                        <>
                            <h2 className="text-3xl font-black text-gray-900 mb-2 text-center tracking-tighter">
                                Criar sua conta
                            </h2>
                            <p className="text-gray-500 text-center mb-8 font-medium">
                                Seja bem-vindo ao futuro da saída escolar.
                            </p>

                            {error && (
                                <div className="bg-red-50 border border-red-100 text-red-600 px-4 py-3 rounded-xl mb-6 text-sm font-bold">
                                    ⚠️ {error}
                                </div>
                            )}

                            <form onSubmit={handleSubmit} className="space-y-4">
                                {/* ... form fields ... */}
                                {/* Note: I need to preserve the form fields here but the replace tool works better with smaller chunks. 
                                    Actually, I'll replace the whole inner part of the div.card.
                                */}
                                <div>
                                    <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">
                                        Nome Completo
                                    </label>
                                    <input
                                        name="nome_completo"
                                        type="text"
                                        value={formData.nome_completo}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-50 rounded-2xl focus:border-primary focus:bg-white focus:ring-0 transition-all font-bold"
                                        placeholder="Seu nome"
                                        required
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">
                                            WhatsApp
                                        </label>
                                        <input
                                            name="telefone"
                                            type="tel"
                                            value={formData.telefone}
                                            onChange={handleChange}
                                            className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-50 rounded-2xl focus:border-primary focus:bg-white focus:ring-0 transition-all font-bold"
                                            placeholder="(00) 00000-0000"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">
                                            Placa do Carro
                                        </label>
                                        <input
                                            name="placa_veiculo"
                                            type="text"
                                            value={formData.placa_veiculo}
                                            onChange={handleChange}
                                            className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-50 rounded-2xl focus:border-primary focus:bg-white focus:ring-0 transition-all font-bold uppercase"
                                            placeholder="ABC-1234"
                                            maxLength={8}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">
                                        E-mail
                                    </label>
                                    <input
                                        name="email"
                                        type="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-50 rounded-2xl focus:border-primary focus:bg-white focus:ring-0 transition-all font-bold"
                                        placeholder="seu@email.com"
                                        required
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">
                                            Senha
                                        </label>
                                        <input
                                            name="password"
                                            type="password"
                                            value={formData.password}
                                            onChange={handleChange}
                                            className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-50 rounded-2xl focus:border-primary focus:bg-white focus:ring-0 transition-all font-bold"
                                            placeholder="••••••"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">
                                            Confirmar
                                        </label>
                                        <input
                                            name="confirmPassword"
                                            type="password"
                                            value={formData.confirmPassword}
                                            onChange={handleChange}
                                            className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-50 rounded-2xl focus:border-primary focus:bg-white focus:ring-0 transition-all font-bold"
                                            placeholder="••••••"
                                            required
                                        />
                                    </div>
                                </div>

                                <Button
                                    type="submit"
                                    variant="primary"
                                    className="w-full py-5 text-xl font-black rounded-2xl shadow-xl shadow-primary/20 mt-4"
                                    loading={loading}
                                >
                                    CRIAR CONTA
                                </Button>
                            </form>

                            <div className="mt-8 text-center text-sm font-bold text-gray-400">
                                <p>
                                    Já tem uma conta?{' '}
                                    <Link to={ROUTES.LOGIN} className="text-primary hover:underline underline-offset-4 decoration-2">
                                        Fazer Login
                                    </Link>
                                </p>
                            </div>
                        </>
                    )}
                </div>

                <div className="mt-8 text-center">
                    <Link to={ROUTES.HOME} className="text-gray-400 hover:text-primary font-bold transition-colors text-sm uppercase tracking-widest">
                        ← Voltar para o início
                    </Link>
                </div>
            </div>
        </div>
    );
}
